/**
 * Extract non-table style tags from HTML (preserves responsive font/weight/align styles)
 * This is needed because DOMParser moves <style> tags to <head>, so we lose them
 * when getting doc.body.innerHTML
 * 
 * Returns: { html: string (with style tags removed), styleTags: string[] (array of style tag contents to preserve) }
 */
export const extractNonTableStyleTags = (html: string): { html: string; styleTags: string[] } => {
  const preservedStyleTags: string[] = [];
  
  // Extract all style tags and check if they should be preserved
  const htmlWithoutStyles = html.replace(/<style[^>]*>([\s\S]*?)<\/style>/g, (match, cssContent) => {
    // Check if this is a table-related style (has data attribute or table selectors)
    const hasTableDataAttr = /data-table-style|data-column-style|data-cell-style|data-row-style/i.test(match);
    const hasTableSelectors = /table\[data-table-id/i.test(cssContent) ||
                              /\.cell-style-/i.test(cssContent) ||
                              /\.col-style-/i.test(cssContent) ||
                              /\.table-style-/i.test(cssContent) ||
                              /\.table-wrapper\[data-for-table/i.test(cssContent);
    
    // Check if it has responsive styles that should be preserved
    const hasResponsiveStyles = /\.responsive-(font|weight|align)-/i.test(cssContent);
    
    // If it has responsive styles and NO table selectors, preserve it entirely
    if (hasResponsiveStyles && !hasTableSelectors && !hasTableDataAttr) {
      preservedStyleTags.push(match);
      return ''; // Remove from HTML, we'll add it back later
    }
    
    // If it has table data attrs, let it be removed (will be regenerated)
    if (hasTableDataAttr) {
      return ''; // Remove - will be regenerated
    }
    
    // If it has table selectors but also responsive styles, extract only responsive styles
    if (hasTableSelectors && hasResponsiveStyles) {
      const preservedCSS = extractNonTableCSS(cssContent);
      if (preservedCSS) {
        preservedStyleTags.push(`<style>${preservedCSS}</style>`);
      }
      return ''; // Remove from HTML, we'll add the preserved part back
    }
    
    // If it has only table selectors, remove it (will be regenerated)
    if (hasTableSelectors) {
      return ''; // Remove - will be regenerated
    }
    
    // For any other style tag (no table selectors, no responsive styles), preserve it
    preservedStyleTags.push(match);
    return ''; // Remove from HTML, we'll add it back later
  });
  
  return { html: htmlWithoutStyles, styleTags: preservedStyleTags };
};

/**
 * Extract only non-table CSS rules from a CSS string
 */
const extractNonTableCSS = (css: string): string => {
  // Split CSS into individual rules (handling media queries as well)
  const ruleRegex = /(@media[^{]*\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\})|([^{}]+\{[^{}]*\})/g;
  const rules: string[] = [];
  let match;
  
  while ((match = ruleRegex.exec(css)) !== null) {
    const rule = match[0].trim();
    // Check if this rule is table-related
    const isTableRule = /table\[data-table-id/i.test(rule) || 
                        /\[data-table-id\]/i.test(rule) ||
                        /\.table-wrapper\[data-for-table/i.test(rule) ||
                        /\.table-style-/i.test(rule) ||
                        /\.col-style-/i.test(rule) ||
                        /\.cell-style-/i.test(rule);
    
    // Keep non-table rules (like responsive font styles)
    if (!isTableRule) {
      rules.push(rule);
    }
  }
  
  return rules.join('\n').trim();
};

/**
 * Helper function to remove table-related CSS rules from a CSS string
 * while preserving non-table CSS rules (like responsive font styles)
 */
const removeTableCSSRules = (css: string): string => {
  // Split CSS into individual rules (handling media queries as well)
  // This regex matches both regular rules and media query blocks
  const ruleRegex = /(@media[^{]*\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\})|([^{}]+\{[^{}]*\})/g;
  const rules: string[] = [];
  let match;
  
  while ((match = ruleRegex.exec(css)) !== null) {
    const rule = match[0].trim();
    // Check if this rule is table-related (targets table elements with data-table-id)
    const isTableRule = /table\[data-table-id/i.test(rule) || 
                        /\[data-table-id\]/i.test(rule) ||
                        /\.table-wrapper\[data-for-table/i.test(rule) ||
                        /\.table-style-/i.test(rule) ||
                        /\.col-style-/i.test(rule) ||
                        /\.cell-style-/i.test(rule);
    
    // Keep non-table rules
    if (!isTableRule) {
      rules.push(rule);
    }
  }
  
  return rules.join('\n').trim();
};

/**
 * Remove style tags from HTML string and clean up empty paragraphs
 */
export const removeStyleTags = (html: string): string => {
  // First, remove style tags with specific data attributes (these are purely table-related)
  let result = html
    .replace(/<style[^>]*data-table-style[^>]*>[\s\S]*?<\/style>/g, '')
    .replace(/<style[^>]*data-column-style[^>]*>[\s\S]*?<\/style>/g, '')
    .replace(/<style[^>]*data-cell-style[^>]*>[\s\S]*?<\/style>/g, '')
    .replace(/<style[^>]*data-row-style[^>]*>[\s\S]*?<\/style>/g, '');
  
  // For generic <style> tags that may contain mixed content (table + non-table CSS),
  // extract and preserve non-table CSS rules instead of removing the entire tag
  result = result.replace(/<style>([\s\S]*?)<\/style>/g, (match, cssContent) => {
    const hasTableSelectors = /table\[data-table-id/i.test(cssContent);
    
    if (hasTableSelectors) {
      // Remove only table-related rules, keep other CSS (like responsive font styles)
      const preservedCSS = removeTableCSSRules(cssContent);
      if (preservedCSS) {
        return `<style>${preservedCSS}</style>`;
      }
      return '';
    }
    
    // No table selectors, keep the entire style tag as is
    return match;
  });
  
  // Remove legacy JSON data attributes (now using individual attributes/inline styles instead)
  result = result.replace(/ data-table-styles="[^"]*"/g, '');
  result = result.replace(/ data-table-settings="[^"]*"/g, '');
  result = result.replace(/ data-column-styles="[^"]*"/g, '');
  result = result.replace(/ data-all-column-settings="[^"]*"/g, '');
  result = result.replace(/ data-column-settings="[^"]*"/g, '');
  result = result.replace(/ data-responsive-widths="[^"]*"/g, '');
  // NOTE: Do NOT remove data-cell-styles and data-cell-settings - they are needed for responsive cell styles!
  // result = result.replace(/ data-cell-styles="[^"]*"/g, '');
  // result = result.replace(/ data-cell-settings="[^"]*"/g, '');
  
  // Clean up empty paragraphs before the table wrapper
  result = result.replace(/<p>\s*<\/p>\s*(?=<div[^>]*data-table-wrapper)/g, '');
  // Clean up all empty paragraphs between style tags and at the end
  result = result.replace(/<p>\s*<\/p>\s*(?=<style|$)/g, '');
  // Clean up consecutive empty paragraphs throughout
  result = result.replace(/(<p>\s*<\/p>\s*){2,}/g, '<p></p>');
  // Clean up orphaned empty paragraphs at the end
  result = result.replace(/(<\/div>|<\/table>)\s*(<p>\s*<\/p>\s*)+$/g, '$1');
  // Clean up empty paragraphs at the start
  result = result.replace(/^(<p>\s*<\/p>\s*)+/, '');
  
  return result;
};

/**
 * Clean up HTML for final output - removes verbose data attributes that are only needed for editing
 * Keeps: data-table-id, class names, style blocks
 * Removes: data-table-settings, data-cell-styles, data-all-column-settings, data-column-settings, etc.
 */
export const cleanupHTMLForOutput = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  
  // Attributes to remove from tables (keep data-table-id for CSS targeting)
  const tableAttrsToRemove = [
    'data-table-settings',
    'data-cell-styles', 
    'data-all-column-settings',
    'data-all-row-settings',
    'data-border-color',
    'data-border-width',
    'data-border-radius',
    'data-border-spacing',
    'data-responsive-widths',
  ];
  
  // Attributes to remove from cells
  const cellAttrsToRemove = [
    'data-cell-settings',
    'data-column-settings',
    'data-cell-class', // Keep the CSS class, remove this redundant attribute
  ];
  
  // Clean tables
  doc.querySelectorAll('table').forEach(table => {
    tableAttrsToRemove.forEach(attr => table.removeAttribute(attr));
  });
  
  // Clean cells
  doc.querySelectorAll('td, th').forEach(cell => {
    cellAttrsToRemove.forEach(attr => cell.removeAttribute(attr));
  });
  
  // Clean wrapper divs
  doc.querySelectorAll('[data-table-wrapper]').forEach(wrapper => {
    wrapper.removeAttribute('data-table-wrapper');
  });
  
  return doc.body.innerHTML;
};

